import React, { useState } from "react"

import { ArrowUpDown, Filter, MapPin, Search, ShieldAlert } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

type Option = { value: string; label: string }

// Cancelled routes are hidden by default and only shown when "Cancelled" is selected here.
const STATUS_OPTIONS: Option[] = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "delayed", label: "Delayed" },
  { value: "ahead", label: "Ahead" },
  { value: "cancelled", label: "Cancelled (show)" },
]

const SORT_TRIGGER_LABELS: Record<string, string> = {
  none: "Sort",
  concern: "Concern",
  most_behind: "Most Behind",
  most_ahead: "Most Ahead",
  route_code: "Route Code",
  driver_name: "Driver Name",
  start_time: "Start Time",
  progress: "Progress",
}

const PLAIN_SORT_OPTIONS: Option[] = [
  { value: "most_behind", label: "Most Behind" },
  { value: "most_ahead", label: "Most Ahead" },
  { value: "route_code", label: "Route Code" },
  { value: "driver_name", label: "Driver Name" },
  { value: "start_time", label: "Start Time" },
  { value: "progress", label: "Progress" },
]

const TRIGGER =
  "h-7 min-w-0 flex-auto justify-center gap-1 border-palette-jungleGreen200 bg-palette-jungleGreen000 px-2 text-xs text-palette-jungleGreen900 [&_svg]:size-3"
const TRIGGER_ACTIVE = "border-blue-200 bg-blue-50"
const ITEM =
  "flex cursor-pointer items-center space-x-2 rounded p-1.5 hover:bg-palette-jungleGreen000"
const INPUT = "h-3.5 w-3.5 border-palette-jungleGreen300 text-blue-600 focus:ring-blue-500"
const LABEL = "text-xs text-palette-jungleGreen900"

function CheckboxMenu({
  icon,
  label,
  options,
  selected,
  onToggle,
  scroll,
}: {
  icon: React.ReactNode
  label: string
  options: Option[]
  selected: string[]
  onToggle: (value: string) => void
  scroll?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn(TRIGGER, selected.length > 0 && TRIGGER_ACTIVE)}>
          {icon}
          <span className="truncate">{selected.length === 0 ? label : `${selected.length}`}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        variant="menu"
        className={cn(scroll && "max-h-64 overflow-y-auto")}
        align="start"
      >
        <div className="space-y-1">
          {options.map((option) => (
            <label key={option.value} className={ITEM}>
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => onToggle(option.value)}
                className={cn(INPUT, "rounded")}
              />
              <span className={LABEL}>{option.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default function RouteFilters({
  searchTerm,
  onSearchChange,
  extraStatusOptions = [],
  statusFilter,
  onStatusToggle,
  depots,
  depotFilter,
  onDepotToggle,
  sortBy,
  onSortChange,
  className,
}: {
  searchTerm: string
  onSearchChange: (term: string) => void
  extraStatusOptions?: Option[]
  statusFilter: string[]
  onStatusToggle: (status: string) => void
  depots: string[]
  depotFilter: string[]
  onDepotToggle: (depot: string) => void
  sortBy: string
  onSortChange: (sortBy: string) => void
  className?: string
}) {
  const [sortOpen, setSortOpen] = useState(false)

  return (
    <div className={cn("w-full space-y-2", className)}>
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-palette-jungleGreen400" />
        <Input
          placeholder="Search routes..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-8 w-full border-palette-jungleGreen200 bg-palette-jungleGreen000 pl-9 text-sm text-palette-jungleGreen900 placeholder:text-palette-jungleGreen400"
        />
      </div>

      <div className="flex w-full gap-1 overflow-hidden">
        <CheckboxMenu
          icon={<Filter size={12} />}
          label="Status"
          options={[...STATUS_OPTIONS, ...extraStatusOptions]}
          selected={statusFilter}
          onToggle={onStatusToggle}
        />
        <CheckboxMenu
          icon={<MapPin size={12} />}
          label="Depot"
          options={depots.map((depot) => ({ value: depot, label: depot }))}
          selected={depotFilter}
          onToggle={onDepotToggle}
          scroll
        />

        <Popover open={sortOpen} onOpenChange={setSortOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn(TRIGGER, sortBy !== "none" && TRIGGER_ACTIVE)}>
              <ArrowUpDown size={12} />
              <span className="truncate">{SORT_TRIGGER_LABELS[sortBy] ?? "Sort"}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent variant="menu" align="start">
            <div className="space-y-1">
              <label className={ITEM} onClick={() => onSortChange("none")}>
                <input
                  type="radio"
                  checked={sortBy === "none"}
                  onChange={() => onSortChange("none")}
                  className={INPUT}
                />
                <span className={LABEL}>None</span>
              </label>
              <label className={cn(ITEM, "bg-rose-50")} onClick={() => onSortChange("concern")}>
                <input
                  type="radio"
                  checked={sortBy === "concern"}
                  onChange={() => onSortChange("concern")}
                  className="h-3.5 w-3.5 border-palette-jungleGreen300 text-rose-600 focus:ring-rose-500"
                />
                <span className="flex items-center gap-1 text-xs font-medium text-rose-700">
                  <ShieldAlert size={10} />
                  Needs Attention
                </span>
              </label>
              <div className="my-1 border-t border-palette-jungleGreen100" />
              {PLAIN_SORT_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={ITEM}
                  onClick={() => onSortChange(option.value)}
                >
                  <input
                    type="radio"
                    checked={sortBy === option.value}
                    onChange={() => onSortChange(option.value)}
                    className={INPUT}
                  />
                  <span className={LABEL}>{option.label}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
